import React from 'react';
import { motion } from 'framer-motion';
import { 
  CalendarCheck, 
  Handshake, 
  CreditCard, 
  Database,
  CheckCircle
} from 'lucide-react';

const conversionSteps = [
  {
    icon: CalendarCheck,
    title: 'Booking Confirmation',
    description: 'Automated scheduling with calendar sync and instant confirmations.',
  },
  {
    icon: Handshake,
    title: 'Accepted Offer',
    description: 'Clear proposals with digital signing and acceptance tracking.',
  },
  {
    icon: CreditCard,
    title: 'Payment System',
    description: 'Seamless checkout with multiple payment options and instant receipts.',
  },
  {
    icon: Database,
    title: 'CRM Integration',
    description: 'Customer data flows into your CRM for complete relationship tracking.',
  },
];

export default function ConversionSection() {
  return (
    <section 
      id="conversion"
      data-testid="conversion-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-[#030303]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(112,0,255,0.08)_0%,_transparent_50%)]" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-purple-400 mb-4 block">
            Stage 3 / Conversion
          </span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 
              data-testid="conversion-headline"
              className="text-4xl md:text-5xl font-bold text-white"
            >
              Prospects Become
              <br />
              <span className="text-purple-400">Customers</span>
            </h2>
            <p className="text-gray-400 max-w-md text-base">
              The moment of transformation - where nurtured leads become paying customers 
              through a frictionless conversion process.
            </p>
          </div>
        </motion.div>
        
        {/* Conversion Flow */}
        <div className="relative max-w-5xl mx-auto">
          {/* Connection line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-purple-500/30 via-purple-500/50 to-purple-500/30 transform -translate-y-1/2" />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {conversionSteps.map((step, index) => (
              <motion.div
                key={index}
                data-testid={`conversion-step-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
                className="relative group"
              >
                {/* Step number */}
                <div className="hidden lg:flex absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 rounded-full bg-purple-500/20 items-center justify-center z-10 border border-purple-500/30">
                  <span className="text-xs font-bold text-purple-400">{index + 1}</span>
                </div>
                
                <div className="glass rounded-2xl p-6 text-center card-hover border border-purple-500/10 h-full">
                  <div className="w-14 h-14 mx-auto rounded-xl bg-purple-500/10 flex items-center justify-center mb-4 group-hover:bg-purple-500/20 transition-colors">
                    <step.icon className="w-7 h-7 text-purple-400" />
                  </div>
                  
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Conversion Result */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-16 flex justify-center"
        >
          <div className="glass-heavy rounded-2xl p-8 max-w-md text-center border border-purple-500/20">
            <motion.div
              animate={{ 
                boxShadow: [
                  '0 0 0 rgba(168, 85, 247, 0)',
                  '0 0 40px rgba(168, 85, 247, 0.3)',
                  '0 0 0 rgba(168, 85, 247, 0)'
                ]
              }}
              transition={{ duration: 3, repeat: Infinity }}
              className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-purple-500/20 to-purple-700/20 flex items-center justify-center mb-4 border border-purple-500/30"
            >
              <CheckCircle className="w-10 h-10 text-purple-400" />
            </motion.div>
            <h3 className="text-xl font-semibold text-white mb-2">
              Customer Activated
            </h3>
            <p className="text-gray-400 text-sm">
              Lead transformed into a paying customer with full CRM tracking
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
